import { Component, effect, input, model, output, TemplateRef, ViewChild, ViewContainerRef } from "@angular/core";
import { Subject, debounceTime } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "app-job-slider",
  standalone: false,
  templateUrl: "./job-slider.component.html",
  styleUrl: "./job-slider.component.less",
})
export class JobSliderComponent {
  // Views for re rendering
  @ViewChild("sliderTemplateContainer", { read: ViewContainerRef })
  containerRef: ViewContainerRef;
  @ViewChild("sliderTemplate", { read: TemplateRef })
  sliderTemplateRef: TemplateRef<any>;

  // Inputs using Signal API
  label = input.required<string>();
  upperLimit = input.required<number>();
  lowerLimit = input<number>(0);
  isMobile = input<boolean>()

  // Model signal allows two-way binding [value]="myVal"
  value = model<number>(0);
  stepValue = model<number>(10);

  // Event emitters for your jobState store
  valueUpdated = output<number>();

  // RxJS stream for debouncing slider movements
  private sliderDebouncer = new Subject<number>();
  private inputDebouncer = new Subject<number>();

  constructor() {
    // Handle the debounced slider logic
    this.sliderDebouncer
      .pipe(
        debounceTime(300), // Wait for user to stop sliding
        takeUntilDestroyed(),
      )
      .subscribe((val) => this.emitUpdate(val));

    this.inputDebouncer
      .pipe(debounceTime(500), takeUntilDestroyed())
      .subscribe((val) => this.emitUpdate(val));

    effect(() => {
      // we want to re-render the slider, when the min/max change. if they aren't changing and only the value itself is, they'll be fine
      // but min max changing cause a rare issue.
      this.upperLimit(); // reading this is basically the trigger that causes the effect to re run
      // our min is currently always 0
      // see rerender for further info
      this.rerender();
    });
  }

  // --- Methods for Use Case 2 (Step Buttons) ---
  adjustStep(direction: "plus" | "minus") {
    const change = direction === "plus" ? this.stepValue() : -this.stepValue();
    const newValue = Math.min(
      Math.max(0, this.value() + change),
      this.upperLimit(),
    );

    this.value.set(newValue);
    this.emitUpdate(newValue); // Instantaneous for step buttons
  }

  // --- Methods for Use Case 3 (Event Handlers) ---
  onSliderChange(val: number) {
    this.value.set(val);
    this.sliderDebouncer.next(val);
  }

  onInputEnter() {
    this.emitUpdate(this.value()); // Instantaneous on Enter key
  }
  onInputArrow() {
    // debounced for arrow keys
    this.inputDebouncer.next(this.value()); // Instantaneous on Enter key
  }

  private emitUpdate(val: number) {
    this.valueUpdated.emit(val);
  }

  rerender() {
    // this is a hack. Why?
    // suppose you iterationValue = 10, and you assign to 10 again. This doesn't rerender the DOM.
    // This is fine, but if the min and max of that slider change, the values get updated but the slider does not update its view
    // so the position of the thumb remains wrong. Doing this, will update the value and rerender. Tho i am only doing this for
    // when assigning the value to the the iteration cause in most other cases it still works and doesn't cause a problem.

    // honestly it could also have worked had I used a subject + observable + subscription kinda thing, but doesn that overcomplicate things.
    this.containerRef?.clear();
    this.containerRef?.createEmbeddedView(this.sliderTemplateRef);
  }
}
